import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    TextField,
    MenuItem,
    Stack,
    IconButton,
    InputAdornment,
    Divider,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Build as WrenchIcon,
    Search as SearchIcon,
    Assignment as TicketIcon
} from '@mui/icons-material';
import serviceTicketService from '../../services/serviceTicketService';
import deviceManagementService from '../../services/deviceManagementService';

const NewServiceTicket = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [devices, setDevices] = useState([]);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        device_id: '',
        issue_type: 'PREVENTIVE_MAINTENANCE',
        priority: 'MEDIUM',
        description: '',
        reported_by: '' // In a real app, this would be the logged-in user ID
    });

    useEffect(() => {
        fetchDevices();
    }, []);

    const fetchDevices = async () => {
        try {
            const response = await deviceManagementService.getDevices();
            setDevices(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to fetch devices:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await serviceTicketService.createTicket(formData);
            if (response.success) {
                navigate(`/service/tickets/${response.data.id}`);
            } else {
                setError(response.message || 'Failed to create ticket');
            }
        } catch (error) {
            console.error('Error creating ticket:', error);
            setError(error.message || 'Error occurred while creating ticket');
        } finally {
            setLoading(false);
        }
    };

    const issueTypes = [
        { value: 'BREAKDOWN', label: 'Breakdown / Repair' },
        { value: 'PREVENTIVE_MAINTENANCE', label: 'Preventive Maintenance' },
        { value: 'INSTALLATION', label: 'Installation' },
        { value: 'INSPECTION', label: 'Pre-Rental Inspection' },
        { value: 'OTHER', label: 'Other' }
    ];

    const priorities = [
        { value: 'LOW', label: 'Low Priority' },
        { value: 'MEDIUM', label: 'Medium Priority' },
        { value: 'HIGH', label: 'High Priority' },
        { value: 'CRITICAL', label: 'Critical / AOG' }
    ];

    return (
        <Box p={3} className="animate-fade-in">
            {/* Header */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <IconButton onClick={() => navigate('/service')} sx={{ bgcolor: 'white', boxShadow: 'var(--shadow-sm)' }}>
                    <ArrowBackIcon />
                </IconButton>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
                <Grid container spacing={4}>
                    <Grid item xs={12} lg={7}>
                        <Card sx={{ borderRadius: '24px', boxShadow: '0 4px 15px -1px rgb(0 0 0 / 0.1)' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Stack spacing={4}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: 'text.secondary', textTransform: 'uppercase' }}>
                                            Equipment Identification
                                        </Typography>
                                        <TextField
                                            select
                                            fullWidth
                                            name="device_id"
                                            label="Select Asset"
                                            value={formData.device_id}
                                            onChange={handleChange}
                                            required
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                        >
                                            <MenuItem value="">Select an asset...</MenuItem>
                                            {devices.map(device => (
                                                <MenuItem key={device.device_id} value={device.device_id}>
                                                    {device.name || device.device_id} ({device.device_id})
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: 'text.secondary', textTransform: 'uppercase' }}>
                                            Service Classification
                                        </Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    name="issue_type"
                                                    label="Maintenance Category"
                                                    value={formData.issue_type}
                                                    onChange={handleChange}
                                                    required
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                                >
                                                    {issueTypes.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    name="priority"
                                                    label="Assigned Priority"
                                                    value={formData.priority}
                                                    onChange={handleChange}
                                                    required
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                                >
                                                    {priorities.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: 'text.secondary', textTransform: 'uppercase' }}>
                                            Fault Diagnostics / Description
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            name="description"
                                            placeholder="Provide detailed information about the issue or maintenance requirements..."
                                            value={formData.description}
                                            onChange={handleChange}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                        />
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        <Card sx={{ borderRadius: '24px', position: 'sticky', top: 20 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>Summary & Action</Typography>

                                <Stack spacing={2} mb={4}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">Process Type:</Typography>
                                        <Typography variant="body2" fontWeight={700}>Digital Ticketing</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">Trigger Mode:</Typography>
                                        <Typography variant="body2" fontWeight={700}>Manual Scheduling</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">Next Step:</Typography>
                                        <Typography variant="body2" color="primary.main" fontWeight={700}>Technician Dispatch</Typography>
                                    </Box>
                                </Stack>

                                <Divider sx={{ mb: 4 }} />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    type="submit"
                                    disabled={loading || !formData.device_id}
                                    className="gradient-primary"
                                    startIcon={<SaveIcon />}
                                    sx={{ borderRadius: '16px', py: 1.5, fontWeight: 800 }}
                                >
                                    {loading ? 'Processing...' : 'Generate Service Ticket'}
                                </Button>

                                <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={2}>
                                    Automated logs will record this action and notify the service department immediately.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default NewServiceTicket;
